import styled, { css } from 'styled-components'

import type { SpacingKey } from '@/@bothrs-theme/types/spacing'
import type { CardProps } from './Card.types'

/**
 * The card frame on its own: border, radius, fill and padding. It sets the
 * frame only; anything about the contents — direction, gap, alignment —
 * belongs to the card itself.
 *
 * For a surface that has to look like a card but needs its own element: `Card`
 * is always a `div`, takes no padding and passes on only `className`, `style`
 * and `onClick`, so it swallows `id`, `aria-*` and `data-*`. Padding is the only
 * knob, so the frame stays one design wherever it is used.
 */
const cardFrameCSS = (padding: SpacingKey = 'Space4') => css`
  ${({ theme }) => `
    padding: ${theme.UI.SpacingPx[padding]};
    border: 1px solid ${theme.Components.Card.Border};
    border-radius: ${theme.UI.BorderRadius.Large}px;
    background-color: ${theme.Components.Card.Fill};
  `}
`

// The state block below restates border and fill per state; the frame supplies
// padding and radius, and the default look for any state that does not.
const StCard = styled.div<Omit<CardProps, 'children'>>`
  ${cardFrameCSS()}
  width: 100%;
  position: relative;

  ${({ active, disabled, onClick, theme }) => {
    if (disabled) {
      return `
        background-color: ${theme.Components.Card.DisabledFill};
        border: 1px solid ${theme.Components.Card.DisabledBorder};
        color: ${theme.Components.Card.DisabledText};
      `
    }

    if (active) {
      return `
        background-color: ${theme.Components.Card.ActiveFill};
        border: 1px solid ${theme.Components.Card.ActiveBorder};
        color: ${theme.Components.Card.ActiveText};
      `
    }
    if (onClick) {
      return `
        background-color: ${theme.Components.Card.Fill};
        border: 1px solid ${theme.Components.Card.Border};
        color: ${theme.Components.Card.Text};
        cursor: pointer;
        &:hover {
          background-color: ${theme.Components.Card.ActiveFill};
          border: 1px solid ${theme.Components.Card.ActiveBorder};
          color: ${theme.Components.Card.ActiveText};
        }
      `
    }

    return `
      background-color: ${theme.Components.Card.Fill};
      border: 1px solid ${theme.Components.Card.Border};
      color: ${theme.Components.Card.Text};
    `
  }}
`

export { cardFrameCSS, StCard }
